/**
 * The Add-step menu (Workflows P5-2): how a step is filed, which steps end a path, the ready-made presets and the
 * names people know the steps by. The trigger and plan gates stay in `workflows.ts` (`isFacingTrigger`,
 * `NON_CONVERSATION_TRIGGERS`, `CONVERSATION_STEP_TYPES`, `UNATTACHED_TRIGGERS`, `WORKFLOW_STEP_MIN_PLAN`); the menu only reads them.
 */
import type { IconName } from "./icons";
import {
  isFacingType,
  WORKFLOW_ACTION_TYPES,
  WORKFLOW_FACING_TYPES,
  WORKFLOW_FLOW_TYPES,
  type WorkflowStepType,
} from "./workflows";

/** The three lists of the menu; a step's tile colour follows its category. */
export const STEP_CATEGORIES = ["content", "actions", "flow"] as const;
export type StepCategory = (typeof STEP_CATEGORIES)[number];

/** Steps filed away from the list they are declared in. "Run a workflow" hands the run to another workflow, so it is flow. */
export const STEP_CATEGORY_OVERRIDES: Readonly<Partial<Record<WorkflowStepType, StepCategory>>> = { run_workflow: "flow" };

const EVERY_STEP_TYPE: readonly WorkflowStepType[] = [...WORKFLOW_FACING_TYPES, ...WORKFLOW_ACTION_TYPES, ...WORKFLOW_FLOW_TYPES];
const FLOW_TYPE_SET: ReadonlySet<string> = new Set(WORKFLOW_FLOW_TYPES);

function categoryOf(type: WorkflowStepType): StepCategory {
  const override = STEP_CATEGORY_OVERRIDES[type];
  if (override) return override;
  if (isFacingType(type)) return "content";
  return FLOW_TYPE_SET.has(type) ? "flow" : "actions";
}

/** Category of every step type, derived from the type lists so a new step is filed without touching this file. */
export const STEP_CATEGORY: Readonly<Record<WorkflowStepType, StepCategory>> = Object.fromEntries(
  EVERY_STEP_TYPE.map((type) => [type, categoryOf(type)]),
) as Record<WorkflowStepType, StepCategory>;

/** Steps that use a model; their tile carries the sparkles icon and the brand fill. Includes steps that arrive later in P5. */
export const AI_STEP_TYPES: ReadonlySet<string> = new Set(["let_assistant_answer", "add_summary_note"]);

/** Steps after which nothing else in the path can run: the run goes to one of the step's own exits, or ends. */
export const TERMINAL_STEP_TYPES: ReadonlySet<WorkflowStepType> = new Set(["reply_buttons", "branch", "go_to_path", "end", "hand_to_person"]);

/** Steps that cannot be added on a trigger that starts before a conversation exists (`UNATTACHED_TRIGGERS`): they hold the run on a timer of the conversation. */
export const UNATTACHED_LOCKED_TYPES: ReadonlySet<WorkflowStepType> = new Set(["wait", "snooze", "add_summary_note"]);

/** A step type with some of its settings already chosen: "Close conversation" is "Set status" with `closed`. */
export const ADD_STEP_PRESET_IDS = ["close_conversation", "resolve_conversation"] as const;
export type AddStepPresetId = (typeof ADD_STEP_PRESET_IDS)[number];

export interface AddStepPreset {
  type: WorkflowStepType;
  status: "closed" | "resolved";
  icon: IconName;
}

export const ADD_STEP_PRESETS: Readonly<Record<AddStepPresetId, AddStepPreset>> = {
  close_conversation: { type: "set_status", status: "closed", icon: "x-circle" },
  resolve_conversation: { type: "set_status", status: "resolved", icon: "check-circle" },
};

/** Each of the three step lists, and "Replace with", shows this many items before "View more". */
export const ADD_STEP_VISIBLE = 6;
/** "Suggested next steps" never shows more than this. */
export const ADD_STEP_SUGGESTED_MAX = 4;
/** Below this width (px) the menu opens as a bottom sheet instead of a popover. */
export const ADD_STEP_SHEET_BELOW = 480;
/** Per viewer: which sections of the menu are folded (`localStorage`, a JSON list of section ids). */
export const ADD_STEP_COLLAPSED_STORAGE_KEY = "sagot.workflows.add-step.collapsed";

/**
 * English names for a step beyond its label, searched whatever the language of the console: Intercom's names, the words
 * people use for the job. Keyed by the step type, or by the preset id. Steps that do not exist yet are ignored.
 */
export const ADD_STEP_ALIASES: Readonly<Record<string, readonly string[]>> = {
  send_message: ["message", "text", "reply", "say"],
  reply_buttons: ["buttons", "quick replies", "choices", "options", "menu"],
  collect_data: ["ask a question", "form", "customer details", "question", "collect customer details"],
  collect_reply: ["customer reply", "wait for reply", "free text", "ask and wait"],
  let_assistant_answer: ["fin", "ai", "ai agent", "bot", "assistant", "answer"],
  show_booking: ["book", "booking", "appointment", "calendar", "meeting", "schedule"],
  show_lead_form: ["lead", "contact form", "capture"],
  hand_to_person: ["handover", "hand over", "human", "agent", "teammate", "transfer", "escalate", "ticket"],
  expected_reply_time: ["reply time", "response time", "set expectations"],
  ask_rating: ["csat", "rating", "feedback", "survey", "stars"],
  show_article: ["article", "help article", "knowledge", "link"],
  verify_email: ["otp", "verify", "email code", "confirm email"],
  send_webhook: ["webhook", "http", "api", "post"],
  http_request: ["data connector", "custom action", "http", "api", "request", "fetch"],
  add_tags: ["tag", "label"],
  remove_tags: ["untag", "tag", "label"],
  assign: ["assignee", "owner", "route", "team", "inbox"],
  set_status: ["open", "waiting", "resolved", "closed", "ticket"],
  close_conversation: ["close", "closed", "end conversation", "archive"],
  resolve_conversation: ["resolve", "resolved", "done", "solved"],
  add_note: ["note", "internal note", "comment"],
  add_summary_note: ["summary", "summarise", "ai note", "recap"],
  update_lead: ["lead", "crm", "stage", "owner"],
  notify_staff: ["notify", "alert", "ping", "email team", "notification"],
  notify_team: ["notify", "alert", "tell", "ping", "email team", "notification", "staff", "mailbox"],
  route_handoff: ["route", "handoff", "who gets the conversation", "assign handoff", "queue"],
  assign_lead_owner: ["lead owner", "owner", "assign lead", "round robin", "route lead"],
  send_to_subscribed_endpoints: ["webhook", "endpoints", "events", "send events", "developer"],
  apply_sla: ["sla", "service level", "deadline"],
  set_priority: ["priority", "urgent"],
  email_visitor: ["email", "mail", "send email"],
  set_attribute: ["attribute", "property", "field", "custom data", "save value"],
  run_workflow: ["reusable", "subflow", "call workflow", "workflow"],
  snooze: ["snooze", "remind me", "later", "pause conversation"],
  set_language: ["language", "locale", "translate"],
  disable_reply: ["lock composer", "no typing", "disable typing", "block reply"],
  enable_reply: ["unlock composer", "allow typing", "enable typing"],
  branch: ["condition", "if", "else", "split", "decision"],
  apply_rules: ["rules", "conditions", "automation"],
  wait: ["delay", "pause", "timer", "sleep", "after"],
  wait_for_webhook: ["callback", "incoming webhook", "external event"],
  go_to_path: ["goto", "jump", "path", "continue on"],
  end: ["stop", "finish", "terminate", "exit"],
};
